<template>
  <div v-if="isDevelopment" class="alert alert-subtle-secondary text-center">
    {{ $t("app.development_environment") }}
  </div>
  <div v-if="isStaging" class="alert alert-subtle-secondary text-center">
    {{ $t("app.staging_environment") }}
  </div>
  <div
    v-if="isManagingAccount"
    class="alert alert-subtle-warning text-center d-flex justify-content-center align-items-center"
  >
    <div>
      {{
        $t("app.managing_client_account", {
          email: accountStore.account.managing_account,
        })
      }}
      (<button
        type="button"
        class="alert-link border-0 bg-transparent p-0"
        @click="stopManaging"
      >
        {{ $t("app.stop_managing") }}</button
      >)
    </div>
  </div>
  <nav class="navbar navbar-top" data-navbar-appearance="darker">
    <div class="container-fluid topnav-container">
      <div class="d-flex align-items-center">
        <router-link to="/" class="navbar-brand topnav-brand">
          <img
            :src="branding.logo"
            :alt="`${branding.name} Logo`"
            class="navbar-logo"
          />
        </router-link>
        <component :is="portal.TopNavAccessory" v-if="portal.TopNavAccessory" />
      </div>
      <ul class="navbar-nav ms-auto">
        <li class="nav-item" v-if="showAuth">
          <button
            v-if="!isAccountAuthenticated"
            class="btn btn-primary"
            @click="login"
          >
            {{ $t("app.log_in") }}
          </button>
          <div v-else class="d-flex align-items-center">
            <button class="btn btn-secondary me-2" @click="navigateToSettings">
              <font-awesome-icon icon="cog" />
            </button>
            <button class="btn btn-secondary" @click="logoutHandler">
              {{ $t("app.log_out") }}
            </button>
          </div>
        </li>
      </ul>
    </div>
  </nav>
</template>
<script setup lang="ts">
import { stopManaging as stopManagingAccount } from "@/admin/manageAccount";
import { getBranding } from "@/branding";
import { useAccountStore } from "@/composables/account/useAccountStore";
import { isWebview } from "@/composables/isWebview";
import { getPortal } from "@/portals";
import { useAuth0 } from "@auth0/auth0-vue";
import { storeToRefs } from "pinia";
import { onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";

const isDevelopment = import.meta.env.DEV;
const isStaging = window.location.hostname === "proov.soldera.org";
const branding = getBranding();
const portal = getPortal();

const accountStore = useAccountStore();
const { isManagingAccount } = storeToRefs(accountStore);
const { globalErrorHandler } = useGlobalHandlers();

const route = useRoute();
const router = useRouter();

const { loginWithRedirect, logout, isAuthenticated } = useAuth0();
const isAccountAuthenticated = ref(isAuthenticated.value);
const showAuth = !isWebview();

const login = () => {
  loginWithRedirect({ appState: { target: route.fullPath } });
};

const logoutHandler = () => {
  logout({ logoutParams: { returnTo: window.location.origin } });
};

const navigateToSettings = () => {
  router.push("/settings");
};

// A client page holds the managed account's data, so leave it rather than show it stale; admin pages
// don't depend on the managed account and can stay.
const stopManaging = async () => {
  try {
    await stopManagingAccount();
    if (!route.path.startsWith("/admin")) {
      await router.push({ name: "AdminIndex" });
    }
  } catch (error) {
    globalErrorHandler(error);
  }
};

// This is a workaround to get the isAuthenticated value from the auth0 client and keep it in sync with auth0 client.
// For some un-fucking-known reason, auth0-vue does not correctly return a properly reactive value for anything from useAuth0().
// Do not remove the following unless you know what you are doing and you are 100% sure you have a better solution.
// If you try to remove it and fail, increment the time wasted here:
//
// Time wasted here (after initial workaround): 2 hours
//
onMounted(async () => {
  isAccountAuthenticated.value = isAuthenticated.value;
});

watch(route, () => {
  isAccountAuthenticated.value = isAuthenticated.value;
});
</script>

<style scoped lang="scss">
.topnav-container {
  padding-left: 1rem;
}

.topnav-brand {
  margin-left: 0;
  padding-top: 0.25rem;

  :deep(.navbar-logo) {
    height: 1.5rem;
  }
}
</style>
